import React, { useEffect, useState } from 'react';
import { RepairService } from '../types/BookingTypes';
import genuinePartsImg from '../assets/genuineparts.png';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: RepairService | null;
}

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  isOpen,
  onClose,
  service
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;
  if (!service) return null;

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const coveredItems = service.warrantyCoveredItems.split(';');
  const notCoveredItems = service.warrantyNotCoveredItems.split(';');

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-40`}
        onClick={onClose}
      />

      {/* Modal - Bottom Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        onTransitionEnd={handleAnimationEnd}
      >
        <div className="bg-white rounded-t-2xl shadow-2xl w-full max-w-md mx-auto flex flex-col" style={{ maxHeight: '85dvh' }}>

          {/* Handle + Header */}
          <div className="flex-shrink-0 px-5 pt-3 pb-3 border-b border-gray-100">
            <div className="w-8 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{service.serviceName}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-500">{service.serviceDuration} mins</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">

            {/* OEM Explanation */}
            {service.serviceName?.toUpperCase().includes('OEM') && (
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fas fa-info text-blue-500 text-[10px]"></i>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-blue-800 mb-1">What does OEM mean?</h4>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      OEM (Original Equipment Manufacturer) parts are made by the <span className="font-semibold">same factories that supply your phone's brand</span>. They look, feel, and perform just like the original part that came with your phone — same brightness, same touch sensitivity, same quality. This is the part we recommend.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Display Type Info */}
            {(service.serviceName?.toLowerCase().includes("display") || service.serviceName?.toLowerCase().includes("screen")) && (
              <div className="bg-yellow-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-yellow-800 uppercase tracking-wide mb-2">Display Type</h4>
                <div className="grid grid-cols-3 gap-2 text-xs text-yellow-800">
                  {service.serviceName?.toLowerCase().includes("oled") ? (
                    <>
                      <div><span className="block text-[10px] text-yellow-600">Fingerprint</span>Works</div>
                      <div><span className="block text-[10px] text-yellow-600">Fitting</span>100%</div>
                      <div><span className="block text-[10px] text-yellow-600">Type</span>OLED</div>
                    </>
                  ) : (
                    <>
                      <div><span className="block text-[10px] text-yellow-600">Fingerprint</span>Not Supported</div>
                      <div><span className="block text-[10px] text-yellow-600">Fitting</span>90-99%</div>
                      <div><span className="block text-[10px] text-yellow-600">Type</span>TFT</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Warranty */}
            <div className="bg-minto-50 rounded-lg p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-minto-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-shield-alt text-minto-600 text-xs"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{service.warrantyName} — {service.warrantyPeriod} Days</p>
                <p className="text-xs text-gray-500 mt-0.5">{service.warrantyTypeDescription}</p>
              </div>
            </div>

            {/* Covered / Not Covered in 2 columns */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Covered</h4>
                <div className="space-y-1.5">
                  {coveredItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <i className="fas fa-check text-green-500 text-[10px] mt-0.5"></i>
                      <span>{item.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Not Covered</h4>
                <div className="space-y-1.5">
                  {notCoveredItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <i className="fas fa-times text-red-400 text-[10px] mt-0.5"></i>
                      <span>{item.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>


            {/* Genuine Parts Image — OEM only */}
            {service.serviceName?.toUpperCase().includes('OEM') && (
              <div>
                <img
                  src={genuinePartsImg}
                  alt="Genuine Parts Verified"
                  className="w-full rounded-lg object-contain"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-100 px-5 py-3">
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-minto-600 text-white rounded-lg font-medium text-sm hover:bg-minto-700 active:scale-[0.98] transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceDetailsModal;
